import { Injectable, Input, Directive } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  UntypedFormArray,
  ValidatorFn
} from '@angular/forms';

import { createOneAndOnlyOneSatisfiesConditionValidator } from '../validators/one-and-only-one-satisfies-condition.validator';
import { createOnlyOneSatisfiesConditionValidator } from '../validators/only-one-satisfies-condition.validator';
import { FormChildBase } from './form-child-base';

interface MultipleChildFormValues<T> {
  items: T[];
}

export enum PrimaryItemValidatorMode {
  none,
  onlyOne,
  oneAndOnlyOne
}

export interface MultipleChildFormSettings<T> {
  primaryItem: {
    validatorMode: PrimaryItemValidatorMode;
    setFirstItemPushedAsPrimary: boolean;
    isPrimaryGetter(item: T): boolean;
    isPrimarySetter(item: T, value: boolean): void;
  };
}

/**
 * Class containing common logic for various formarrays
 */
@Directive()
@Injectable()
export abstract class MultipleChildFormBase<T> extends FormChildBase<
  T[],
  MultipleChildFormValues<T>
> {
  itemsFormArray: FormArray;

  @Input() title: string;

  @Input() icon: string | null | undefined;

  @Input() titleMode: 'primary' | 'secondary' | null | undefined;

  protected generateFormGroupValuesFromModel(model: T[] | null): MultipleChildFormValues<T> {
    const formValues: MultipleChildFormValues<T> = {
      items: model || []
    };
    return formValues;
  }
  protected generateCopyOfModel(model: T[] | null): T[] {
    if (!model) {
      return [];
    }
    return model.map(this.copySingleModelItem);
  }
  protected abstract copySingleModelItem(modelItem: T): T;

  protected abstract generateNewModelItem(): T;

  protected abstract generateMultipleChildFormSettings(): MultipleChildFormSettings<T> | null;

  protected updateModelCopyUsingFormValues(
    modelCopy: T[],
    formValues: MultipleChildFormValues<T>
  ): T[] {
    while (modelCopy.length > 0) {
      modelCopy.pop();
    }
    for (const item of formValues.items) {
      modelCopy.push(this.copySingleModelItem(item));
    }
    return modelCopy;
  }
  protected generateEmptyForm(_fb: FormBuilder): FormGroup {
    const itemsFormArray = new UntypedFormArray([]);
    const settings = this.generateMultipleChildFormSettings();
    if (settings && settings.primaryItem.validatorMode !== PrimaryItemValidatorMode.none) {
      const conditionToSatisfy = (item: T) =>
        !!(item && settings.primaryItem.isPrimaryGetter(item));
      if (settings.primaryItem.validatorMode === PrimaryItemValidatorMode.oneAndOnlyOne) {
        const oneAndOnlyOnePrimaryItemValidator: ValidatorFn =
          createOneAndOnlyOneSatisfiesConditionValidator<T>(
            conditionToSatisfy,
            false,
            'multiplePrimary',
            'noPrimary'
          );
        itemsFormArray.setValidators([oneAndOnlyOnePrimaryItemValidator]);
      } else if (settings.primaryItem.validatorMode === PrimaryItemValidatorMode.onlyOne) {
        const onlyOnePrimaryItemValidator: ValidatorFn = createOnlyOneSatisfiesConditionValidator(
          conditionToSatisfy,
          'multiplePrimary'
        );
        itemsFormArray.setValidators([onlyOnePrimaryItemValidator]);
      }
    }

    const formGroup = new FormGroup({
      items: itemsFormArray
    });
    this.itemsFormArray = itemsFormArray;
    return formGroup;
  }

  protected applyFormGroupValuesToForm(_form: FormGroup, formValues: MultipleChildFormValues<T>) {
    //synchronize lengths
    while (this.itemsFormArray.length > formValues.items.length) {
      this.itemsFormArray.removeAt(this.itemsFormArray.length - 1);
    }
    while (this.itemsFormArray.length < formValues.items.length) {
      this.pushNewItem(true);
    }
    //synchronize values
    for (let i = 0; i < this.itemsFormArray.length; i++) {
      this.itemsFormArray.at(i).setValue(formValues.items[i]);
    }
  }

  pushNewItem(forBoundValues?: boolean) {
    const newItemFormControl = new FormControl();
    const initialValue = this.generateNewModelItem();
    const settings = this.generateMultipleChildFormSettings();
    if (
      settings &&
      settings.primaryItem.setFirstItemPushedAsPrimary &&
      this.itemsFormArray.length === 0
    ) {
      settings.primaryItem.isPrimarySetter(initialValue, true);
    }
    newItemFormControl.setValue(initialValue);
    this.itemsFormArray.insert(0, newItemFormControl);
    if (!forBoundValues) {
      //add via "Add" button or similar, force a validation update
      //this is necessary due to an apparent timing issue with ancestor form validation state
      //when dynamically adding form controls with their own validation logic attached
      this.forceValidationUpdateOnAncestorForms();
    }
  }

  handlePrimaryChecked(index: number) {
    //set all others to isPrimary: false
    const settings = this.generateMultipleChildFormSettings();
    for (let i = 0; i < this.itemsFormArray.length; i++) {
      if (i !== index) {
        const controlToUpdate = this.itemsFormArray.at(i);
        const updatedItem =
          controlToUpdate.value == null
            ? this.generateNewModelItem()
            : this.copySingleModelItem(controlToUpdate.value);
        if (settings && settings.primaryItem.validatorMode !== PrimaryItemValidatorMode.none) {
          settings.primaryItem.isPrimarySetter(updatedItem, false);
        }
        controlToUpdate.setValue(updatedItem);
      }
    }
  }

  removeItem(index: number) {
    if(this.title === 'Phone' && this.itemsFormArray.controls.length === 1) {
      this.itemsFormArray.setErrors({atLeastOnePhoneNumber: true})
    } else {
      this.itemsFormArray.removeAt(index);
    }
  }
}
